/** Gap between a trigger field and the dropdown it opens. A number because Popper offsets take px. */
export const DROPDOWN_TRIGGER_GAP_PX = 4;
export const DROPDOWN_LIST_PADDING = '0.375rem';
/** Keeps a one- or two-item action menu ("Delete") from shrinking to the width of its label. */
export const DROPDOWN_MENU_MIN_WIDTH = '12rem';
export const DROPDOWN_OPTION_PADDING_X = '0.5625rem';
/** Icon size in a default v2 menu row; a filter glyph above the rows matches it. */
export const DROPDOWN_MENU_ICON_SIZE = '0.8125rem';
/** Font size of a small v2 menu row; help text under small rows matches it. */
export const DROPDOWN_OPTION_FONT_SIZE_SMALL = '0.71875rem';
/** Size of a trigger's trailing chevron and clear glyphs. */
export const DROPDOWN_INDICATOR_SIZE = '1rem';
/** Distance from a trigger's right edge to its chevron (SelectTrigger's right padding). */
export const DROPDOWN_INDICATOR_INSET = '0.5625rem';
/** Size of the check that ends a picked dropdown row (SelectMenu's `OptionCheck`). */
export const DROPDOWN_CHECK_SIZE = '1rem';
// lucide `check`, used as a mask so the glyph takes the row's check colour.
const CHECK_GLYPH_MASK = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6 9 17l-5-5'/%3E%3C/svg%3E\")";
/** Base surface for a V2 dropdown Paper (SelectMenu's Popover, AppMenu's Menu, the MuiMenu theme override, …). */
export const getDropdownSurfaceBackground = (theme) => theme.palette.mode === 'dark'
    ? theme.palette.custom.OV.sys.surfaceContainer
    : theme.palette.custom.OV.sys.surfaceContainerLowest;
/** Hover/selected row background for a V2 dropdown menu item. */
export const getDropdownSurfaceActiveBackground = (theme) => theme.palette.mode === 'dark'
    ? theme.palette.custom.OV.sys.surfaceContainerHighest
    : theme.palette.custom.OV.sys.surfaceContainer;
/** The card every dropdown sits on: SelectMenu and MUI Menu. */
export const getDropdownPaperStyles = (theme) => ({
    backgroundColor: getDropdownSurfaceBackground(theme),
    // Paper paints a white elevation overlay over the token in dark mode,
    // which would lift this surface above the other dropdowns'.
    backgroundImage: 'none',
    border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    borderRadius: '0.75rem',
    boxShadow: theme.shadows[8],
});
/** Geometry of one dropdown row; the highlight comes from `getDropdownSurfaceActiveBackground`. */
export const getDropdownOptionStyles = () => ({
    gap: '0.625rem',
    padding: `0.5rem ${DROPDOWN_OPTION_PADDING_X}`,
    borderRadius: '0.5rem',
    cursor: 'pointer',
});
/**
 * The check that marks a picked option in every v2 dropdown: SelectMenu's `OptionCheckbox` renders
 * it, and MUI menu rows draw it through `getDropdownPickedRowStyles`. Its
 * colour is `currentColor`, so a call site can retune it with `color`.
 */
export const getDropdownCheckGlyphStyles = (theme) => {
    const mask = `${CHECK_GLYPH_MASK} right center / ${DROPDOWN_CHECK_SIZE} no-repeat`;
    return {
        display: 'inline-block',
        flexShrink: 0,
        marginLeft: 'auto',
        // Clearance from a truncating label.
        paddingLeft: '0.75rem',
        width: DROPDOWN_CHECK_SIZE,
        height: DROPDOWN_CHECK_SIZE,
        boxSizing: 'content-box',
        color: theme.palette.custom.OV.sys.primary,
        backgroundColor: 'currentColor',
        WebkitMask: mask,
        mask,
    };
};
/** A picked MUI menu row: no tint, the shared check at the end. A row with its own checkbox keeps that. */
export const getDropdownPickedRowStyles = (theme) => ({
    backgroundColor: 'transparent',
    '&::after': { content: '""', ...getDropdownCheckGlyphStyles(theme) },
    '&:has(.MuiCheckbox-root, .MuiRadio-root)::after': { content: 'none' },
});
export const getDropdownOptionLabelStyles = (theme) => ({
    fontSize: '0.78125rem',
    fontWeight: 600,
    lineHeight: '1.125rem',
    color: theme.palette.custom.OV.sys.onBackground,
});
export const getDropdownGroupHeadingStyles = (theme) => ({
    // The negative `top` cancels the list's padding so rows can't peek above a pinned heading.
    position: 'sticky',
    top: `-${DROPDOWN_LIST_PADDING}`,
    zIndex: 1,
    padding: `0.5rem ${DROPDOWN_OPTION_PADDING_X} 0.125rem`,
    backgroundColor: getDropdownSurfaceBackground(theme),
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
});
/** A trigger's trailing glyph slot (chevron, clear), sized and coloured like SelectTrigger's. */
export const getDropdownIndicatorStyles = (theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    '& svg': { width: DROPDOWN_INDICATOR_SIZE, height: DROPDOWN_INDICATOR_SIZE },
});
/** The clear glyph darkens on hover; callers decide when it is shown. */
export const getDropdownClearIndicatorStyles = (theme) => ({
    ...getDropdownIndicatorStyles(theme),
    width: DROPDOWN_INDICATOR_SIZE,
    height: DROPDOWN_INDICATOR_SIZE,
    borderRadius: '50%',
    transition: 'opacity 120ms ease, color 120ms ease',
    '&:hover': {
        color: theme.palette.custom.OV.sys.onBackground,
        backgroundColor: 'transparent',
    },
});
/** Loading / empty / load-more line inside a dropdown list. */
export const getDropdownStatusStyles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8125rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
});
